import { ActionIcon, Badge, Button, Stack, Tooltip } from "@mantine/core";
import { GridColDef } from "@mui/x-data-grid";
import { PencilIcon, PlusIcon, TrashIcon, WarningIcon } from "@phosphor-icons/react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import toast from "react-hot-toast";

import { DataGridBase, useDataGridBaseParams } from "@/components/DataGridBase";
import { DashboardComponents } from "@/features/dashboard/_components/dashboard";
import { useSession } from "@/hooks";
import { Role, ShippingCompanies, useDeleteShippingCompanyMutation, useListShippingCompaniesInfiniteQuery } from "@/store";
import { requireRoles } from "@/utils/requiredRoles";

export const Route = createFileRoute("/_private/dashboard/_base/transportadoras/")({
  component: RouteComponent,
  beforeLoad: requireRoles({
    allowed: [Role.Administrador, Role.Usuario]
  })
});

function RouteComponent() {
  const { role, isAdminRoot } = useSession();
  const [query] = useDataGridBaseParams();
  const navigate = useNavigate();

  const [deleteShippingCompany] = useDeleteShippingCompanyMutation();
  const { data, isFetching, hasNextPage, fetchNextPage, refetch } = useListShippingCompaniesInfiniteQuery({
    params: {
      filter: query?.quickFilterValues?.join(" ") ?? "",
      pageSize: 50
    }
  });
  const rows = data?.pages.flatMap((page) => page.data) || [];

  const handleDelete = async (id: number) => {
    if (role === Role.Usuario) return toast.error("Você não tem permissão para excluir transportadoras.");

    await deleteShippingCompany({ id });
  };

  const columns: GridColDef<ShippingCompanies>[] = [
    { field: "id", headerName: "ID", type: "number", align: "center", headerAlign: "center" },
    {
      field: "name",
      headerName: "Nome",
      minWidth: 150,
      flex: 1,
      display: "flex",
      type: "string",
      renderCell: ({ value, row }) => (
        <div className="flex flex-col">
          <p>
            <strong>Codigo:</strong> {row.code}
          </p>
          <p>{value}</p>
        </div>
      )
    },
    { field: "cnpj", headerName: "CNPJ", minWidth: 150, flex: 1, type: "string" },
    { field: "uf", headerName: "UF", minWidth: 80, type: "string", align: "center", headerAlign: "center" },
    { field: "city", headerName: "Cidade", minWidth: 100, type: "string" },
    {
      field: "isEnabled",
      headerName: "Status",
      minWidth: 100,
      type: "actions",
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => {
        const badgeValues = {
          color: value ? "green" : "red",
          text: value ? "Ativo" : "Inativo"
        };

        return (
          <Badge color={badgeValues.color} variant="light">
            {badgeValues.text}
          </Badge>
        );
      }
    },
    {
      field: "actions",
      headerName: "Ações",
      type: "actions",
      minWidth: 120,
      renderCell: ({ row }) => {
        //TODO rever retorno de user para validar se a transportadora possui usuário
        const hasUser = row.user?.length > 0;
        /* console.log(row.user.length > 0 ? row.user : {}) */

        return (
          <ActionIcon.Group>
            <Tooltip label="Editar Transportadora">
              <ActionIcon variant="light" color="yellow" onClick={() => navigate({ to: "/dashboard/transportadoras/editar/$id/modal", params: { id: row.id } })}>
                <PencilIcon />
              </ActionIcon>
            </Tooltip>

            {!hasUser && (
              <Tooltip label="Esta Transportadora não possui nenhum usuário cadastrado!">
                <ActionIcon variant="filled" color="red">
                  <WarningIcon />
                </ActionIcon>
              </Tooltip>
            )}

            <Tooltip label="Excluir Transportadora">
              <ActionIcon variant="light" color="red" onClick={() => handleDelete(row.id)}>
                <TrashIcon />
              </ActionIcon>
            </Tooltip>
          </ActionIcon.Group>
        );
      }
    }
  ];

  return (
    <Stack>
      <div className="flex flex-wrap items-center justify-between">
        <DashboardComponents.Title>Gestão de Transportadoras</DashboardComponents.Title>

        <div className="flex flex-1 justify-end gap-2">
          <DashboardComponents.RefreshButton tooltipProps={{ label: "Atualizar Transportadoras" }} onClick={refetch} loading={isFetching} />

          <Button onClick={fetchNextPage} disabled={!hasNextPage || isFetching} className="max-md:w-full!">
            Carregar Mais
          </Button>

          {isAdminRoot && (
            <Tooltip label="Cadastrar nova Transportadora">
              <Link to="/dashboard/transportadoras/cadastrar/modal">
                <ActionIcon variant="filled" size="xl">
                  <PlusIcon />
                </ActionIcon>
              </Link>
            </Tooltip>
          )}
        </div>
      </div>

      <DataGridBase
        columnVisibilityModel={{
          id: false,
          actions: role !== Role.Usuario && isAdminRoot
        }}
        rows={rows}
        columns={columns}
        loading={isFetching}
      />
    </Stack>
  );
}
